import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { authLogin, authRegister, authMe, authLogout, setToken, getToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // Authenticated either by JWT (localStorage) or Google session cookie.
    try {
      const me = await authMe();
      setUser(me);
    } catch {
      if (getToken()) setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from Google OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const data = await authLogin({ email, password });
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password) => {
    const data = await authRegister({ name, email, password });
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  // Called by AuthCallback after a successful Google session exchange.
  const setSessionUser = (u) => setUser(u);

  const logout = async () => {
    setToken(null);
    setUser(null);
    try {
      await authLogout();
    } catch {
      /* ignore */
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, premium: !!user?.subscription?.premium, entitlement: user?.subscription || null, login, register, logout, refresh, setSessionUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
